/**
 * Setlists (F-BAND-05): ordered arrangements (projects) with optional
 * per-entry key/tempo overrides. Opening an entry loads the project and
 * applies overrides as ONE undoable edit, with a notice — never silently.
 */
import { AppError, errorMessage } from "../core/errors";
import { createId } from "../core/ids";
import { CURRENT_SETLIST_VERSION, SETLIST_FORMAT, type Setlist, type SetlistEntry } from "../project/schema";
import type { ProjectSummary } from "../project/storage";
import { keyName, transposeKey } from "../theory/key";
import type { EngineController } from "./controller";
import type { Library } from "./library";
import { Store } from "./store";

export interface SetlistState {
  loaded: boolean;
  setlists: Setlist[];
  songs: ProjectSummary[];
  /** The setlist being performed and the current position in it. */
  active: { setlistId: string; index: number } | null;
}

export type ConfirmDiscard = () => boolean;

export class SetlistService {
  readonly state = new Store<SetlistState>({ loaded: false, setlists: [], songs: [], active: null });

  constructor(private c: EngineController, private library: Library) {
    // Keep song summaries current when a project is saved.
    let lastSaved = c.doc.get().lastSavedAt;
    c.doc.subscribe(() => {
      const now = c.doc.get().lastSavedAt;
      if (now && now !== lastSaved) void this.refreshSongs();
      lastSaved = now;
    });
  }

  private get store() {
    if (!this.c.storage) throw new AppError("STORAGE_ERROR", "Setlists need local storage, which is unavailable in this browser session.");
    return this.c.storage;
  }

  async load(): Promise<void> {
    if (!this.c.storage) { this.patch({ loaded: true }); return; }
    try {
      const [setlists, songs] = await Promise.all([this.c.storage.listSetlists(), this.c.storage.listProjects()]);
      this.patch({ loaded: true, setlists, songs });
    } catch (err) {
      this.c.notify("warning", `Setlists could not be loaded: ${errorMessage(err)}`);
      this.patch({ loaded: true });
    }
  }

  async refreshSongs(): Promise<void> {
    if (this.c.storage) this.patch({ songs: await this.c.storage.listProjects() });
  }

  get(id: string): Setlist | undefined {
    return this.state.get().setlists.find((s) => s.id === id);
  }

  song(projectId: string): ProjectSummary | undefined {
    return this.state.get().songs.find((s) => s.id === projectId);
  }

  async create(name: string, date: string | null = null): Promise<Setlist | null> {
    const now = new Date().toISOString();
    return this.persist({
      format: SETLIST_FORMAT, schemaVersion: CURRENT_SETLIST_VERSION, id: createId(), name: name.trim() || "New setlist",
      date, entries: [], createdAt: now, updatedAt: now, revision: 0,
    });
  }

  async update(id: string, recipe: (s: Setlist) => Setlist): Promise<Setlist | null> {
    const current = this.get(id);
    return current ? this.persist(recipe(current)) : null;
  }

  async addSong(setlistId: string, projectId: string): Promise<void> {
    await this.update(setlistId, (s) => ({ ...s, entries: [...s.entries, { id: createId(), projectId, transposeSemitones: null, tempoBpm: null, notes: "" }] }));
  }

  async updateEntry(setlistId: string, entryId: string, patch: Partial<Omit<SetlistEntry, "id" | "projectId">>): Promise<void> {
    await this.update(setlistId, (s) => ({ ...s, entries: s.entries.map((e) => (e.id === entryId ? { ...e, ...patch } : e)) }));
  }

  async removeEntry(setlistId: string, entryId: string): Promise<void> {
    await this.update(setlistId, (s) => ({ ...s, entries: s.entries.filter((e) => e.id !== entryId) }));
  }

  /** Move an entry by `delta` positions (−1 = up). Keeps the active position on the same song. */
  async moveEntry(setlistId: string, entryId: string, delta: number): Promise<void> {
    const s = this.get(setlistId);
    if (!s) return;
    const from = s.entries.findIndex((e) => e.id === entryId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= s.entries.length) return;
    const entries = [...s.entries];
    const [moved] = entries.splice(from, 1);
    entries.splice(to, 0, moved!);
    const activeEntry = this.activeEntry();
    await this.update(setlistId, (x) => ({ ...x, entries }));
    const active = this.state.get().active;
    if (active?.setlistId === setlistId && activeEntry) this.patch({ active: { setlistId, index: entries.findIndex((e) => e.id === activeEntry.id) } });
  }

  async remove(id: string): Promise<void> {
    try {
      await this.store.deleteSetlist(id);
      const st = this.state.get();
      this.patch({ setlists: st.setlists.filter((s) => s.id !== id), active: st.active?.setlistId === id ? null : st.active });
    } catch (err) { this.c.notify("error", errorMessage(err)); }
  }

  activeEntry(): SetlistEntry | null {
    const a = this.state.get().active;
    return a ? this.get(a.setlistId)?.entries[a.index] ?? null : null;
  }

  /**
   * Open entry `index`: load its arrangement, then apply overrides as one
   * undoable edit and say so. Returns false if cancelled or failed.
   */
  async open(setlistId: string, index: number, confirmDiscard: ConfirmDiscard): Promise<boolean> {
    const s = this.get(setlistId);
    const entry = s?.entries[index];
    if (!s || !entry) return false;
    if (this.c.doc.get().dirty && !confirmDiscard()) return false;
    this.patch({ active: { setlistId, index } });
    if (!this.song(entry.projectId)) {
      this.c.notify("warning", `Song ${index + 1} in "${s.name}" refers to an arrangement that is no longer saved in this browser.`);
      return false;
    }
    if (!(await this.library.open(entry.projectId))) return false;
    const changes: string[] = [];
    const p = this.c.project.get();
    if (entry.transposeSemitones !== null && entry.transposeSemitones !== p.song.transposeSemitones) {
      changes.push(`key ${keyName(transposeKey(p.song.key, entry.transposeSemitones))} (${entry.transposeSemitones > 0 ? "+" : ""}${entry.transposeSemitones})`);
    }
    if (entry.tempoBpm !== null && entry.tempoBpm !== p.song.tempoBpm) changes.push(`${entry.tempoBpm} BPM`);
    if (changes.length) {
      this.c.updateProject((proj) => ({
        ...proj,
        song: { ...proj.song, transposeSemitones: entry.transposeSemitones ?? proj.song.transposeSemitones, tempoBpm: entry.tempoBpm ?? proj.song.tempoBpm },
      }));
      this.c.notify("info", `Setlist settings applied: ${changes.join(", ")}. Save to keep them in the arrangement, or Undo.`);
    }
    return true;
  }

  async step(delta: 1 | -1, confirmDiscard: ConfirmDiscard): Promise<boolean> {
    const a = this.state.get().active;
    if (!a) return false;
    return this.open(a.setlistId, a.index + delta, confirmDiscard);
  }

  end(): void {
    this.patch({ active: null });
  }

  private async persist(s: Setlist): Promise<Setlist | null> {
    try {
      const saved = await this.store.saveSetlist(s);
      const others = this.state.get().setlists.filter((x) => x.id !== saved.id);
      this.patch({ setlists: [saved, ...others].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "") || b.updatedAt.localeCompare(a.updatedAt)) });
      return saved;
    } catch (err) {
      this.c.notify("error", errorMessage(err));
      return null;
    }
  }

  private patch(p: Partial<SetlistState>): void {
    this.state.set({ ...this.state.get(), ...p });
  }
}
